"use client";

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Image from "next/image";
import { usePasskey } from "@/contexts/PasskeyContext";
import { recordActivity } from "@/services/activity.service";
import { TESTNET_TOKENS } from "@/lib/hoops-contracts";
import type { SwapQuote } from "@/lib/hoops-passkey-kit";

// ── Constants ───────────────────────────────────────────────

const TOKEN_UNIT = 10_000_000;

interface SwapToken {
  address: string;
  symbol: string;
  name: string;
  icon: string;
  invert?: boolean;
}

const SWAP_TOKENS: SwapToken[] = [
  { address: TESTNET_TOKENS.XLM, symbol: "XLM", name: "Stellar Lumens", icon: "/token-logos/xlm.png", invert: true },
  { address: TESTNET_TOKENS.USDC, symbol: "USDC", name: "USD Coin", icon: "/icons/tokens/usdc.png" },
];

const ADAPTER_META: Record<number, { name: string; logo: string; fee: string }> = {
  0: { name: "Aquarius", logo: "/images/protocols/aqua_round.svg", fee: "0.25%" },
  1: { name: "Comet", logo: "/images/protocols/comet_round.svg", fee: "0.30%" },
  2: { name: "Phoenix", logo: "/images/protocols/phoenix_round.svg", fee: "0.30%" },
  3: { name: "Soroswap", logo: "/images/protocols/soroswap_round.svg", fee: "0.30%" },
};

const SLIPPAGE_PRESETS = [0.1, 0.5, 1.0];

type SwapState = "idle" | "quoting" | "quoted" | "signing" | "submitted" | "success" | "error";

// ── Token Icon ──────────────────────────────────────────────

function TokenIcon({ token, size = 20 }: { token: SwapToken; size?: number }) {
  return (
    <Image
      src={token.icon}
      alt={token.name}
      width={size}
      height={size}
      className="rounded-full"
      style={token.invert ? { filter: "brightness(0) invert(1)" } : undefined}
    />
  );
}

// ── Token Selector ──────────────────────────────────────────

function TokenDropdown({
  selected, exclude, onSelect, open, setOpen, label,
}: {
  selected: SwapToken; exclude: SwapToken; onSelect: (t: SwapToken) => void;
  open: boolean; setOpen: (v: boolean) => void; label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => { document.removeEventListener("mousedown", handleClick); };
  }, [open, setOpen]);

  const available = SWAP_TOKENS.filter((t) => t.address !== exclude.address);

  return (
    <div ref={ref} className="relative shrink-0">
      <button onClick={() => { setOpen(!open); }} aria-expanded={open} aria-haspopup="listbox" aria-label={`${label}: ${selected.symbol}`}
        className="inline-flex items-center gap-[6px] h-[32px] pl-[6px] pr-[10px] rounded-full transition-all duration-150"
        style={{ background: open ? "var(--surface-hi)" : "var(--surface-2)", border: `1px solid ${open ? "var(--border-strong)" : "var(--border-subtle)"}` }}>
        <TokenIcon token={selected} size={20} />
        <span className="text-[12px] tracking-[0.02em] font-medium" style={{ color: "var(--text-primary)" }}>{selected.symbol}</span>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ color: "var(--text-secondary)" }} className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div role="listbox" aria-label="Select token"
          className="absolute top-full left-0 mt-[6px] py-[5px] rounded-[var(--r)] w-[180px] z-50 overflow-hidden"
          style={{ background: "var(--surface-hi)", border: "1px solid var(--border-default)", boxShadow: "0 12px 32px rgba(0,0,0,0.5)" }}>
          <div className="px-[12px] py-[4px] mb-[2px]">
            <span className="text-[8px] tracking-[0.14em] font-semibold" style={{ color: "var(--text-secondary)" }}>SELECT TOKEN</span>
          </div>
          {available.map((token) => {
            const isSelected = token.address === selected.address;
            return (
              <button key={token.address} role="option" aria-selected={isSelected}
                className="flex items-center gap-[8px] w-full px-[12px] py-[8px] text-left transition-colors duration-100 hover:bg-[rgba(255,255,255,0.05)]"
                onClick={() => { onSelect(token); setOpen(false); }}>
                <TokenIcon token={token} size={22} />
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] font-medium block leading-tight" style={{ color: isSelected ? "var(--accent)" : "var(--text-primary)" }}>{token.symbol}</span>
                  <span className="text-[9px] block leading-tight mt-[1px]" style={{ color: "var(--text-secondary)" }}>{token.name}</span>
                </div>
                {isSelected && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Format helpers ──────────────────────────────────────────

function fmtBalance(stroops: bigint): string {
  const val = Number(stroops) / TOKEN_UNIT;
  return val >= 1000 ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : val.toFixed(2);
}

// ── Main Component ──────────────────────────────────────────

export default function SwapComponent() {
  const { isConnected, swap, queryBalance, getSwapQuotes } = usePasskey();

  // Token selection
  const [payToken, setPayToken] = useState(SWAP_TOKENS[0]);
  const [receiveToken, setReceiveToken] = useState(SWAP_TOKENS[1]);
  const [payAmount, setPayAmount] = useState("");
  const [payDropdownOpen, setPayDropdownOpen] = useState(false);
  const [receiveDropdownOpen, setReceiveDropdownOpen] = useState(false);
  const [slippageTolerance, setSlippageTolerance] = useState(0.5);

  // Balances
  const [payBalance, setPayBalance] = useState<bigint>(0n);
  const [receiveBalance, setReceiveBalance] = useState<bigint>(0n);

  // Quotes
  const [quotes, setQuotes] = useState<SwapQuote[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<SwapQuote | null>(null);
  const [quotesLoading, setQuotesLoading] = useState(false);

  // Swap execution
  const [swapState, setSwapState] = useState<SwapState>("idle");
  const [txHash, setTxHash] = useState<string | null>(null);
  const [swapError, setSwapError] = useState<string | null>(null);

  const parsedPay = payAmount ? parseFloat(payAmount) : 0;

  // ── Balance fetching ────────────────────────────────────
  const refreshBalances = useCallback(async () => {
    if (!isConnected) return;
    const [b0, b1] = await Promise.all([
      queryBalance(payToken.address),
      queryBalance(receiveToken.address),
    ]);
    setPayBalance(b0);
    setReceiveBalance(b1);
  }, [isConnected, payToken.address, receiveToken.address, queryBalance]);

  useEffect(() => { void refreshBalances(); }, [refreshBalances]);

  // ── Quote fetching (debounced) ──────────────────────────
  useEffect(() => {
    if (!parsedPay || parsedPay <= 0 || !isConnected) {
      setQuotes([]);
      setSelectedQuote(null);
      return;
    }
    setQuotesLoading(true);
    const timer = setTimeout(() => { void (async () => {
      try {
        const amount = BigInt(Math.round(parsedPay * TOKEN_UNIT));
        const results = await getSwapQuotes(payToken.address, receiveToken.address, amount);
        setQuotes(results);
        if (results.length > 0) {
          const best = results.reduce((a, b) => Number(b.amount_out) > Number(a.amount_out) ? b : a);
          setSelectedQuote(best);
        } else {
          setSelectedQuote(null);
        }
      } catch {
        setQuotes([]);
        setSelectedQuote(null);
      }
      setQuotesLoading(false);
    })(); }, 500);
    return () => { clearTimeout(timer); };
  }, [parsedPay, payToken.address, receiveToken.address, isConnected, getSwapQuotes]);

  // ── Derived values ──────────────────────────────────────
  const receiveAmount = selectedQuote ? (Number(selectedQuote.amount_out) / TOKEN_UNIT).toFixed(2) : "0.00";
  const minReceived = selectedQuote
    ? (Number(selectedQuote.amount_out) / TOKEN_UNIT * (1 - slippageTolerance / 100)).toFixed(2) : "0.00";
  const rate = selectedQuote && Number(selectedQuote.amount_in) > 0
    ? (Number(selectedQuote.amount_out) / Number(selectedQuote.amount_in)).toFixed(6) : "—";

  // Sort quotes by output (best first)
  const sortedQuotes = useMemo(() => [...quotes].sort((a, b) => Number(b.amount_out) - Number(a.amount_out)), [quotes]);

  const handleFlip = () => {
    setPayToken(receiveToken);
    setReceiveToken(payToken);
    setPayAmount("");
    setQuotes([]);
    setSelectedQuote(null);
  };

  // ── Swap execution ──────────────────────────────────────
  const handleSwap = async () => {
    if (!selectedQuote || !isConnected || !parsedPay) return;

    setSwapState("signing");
    setSwapError(null);
    try {
      const amount = BigInt(Math.round(parsedPay * TOKEN_UNIT));
      const deadline = Math.floor(Date.now() / 1000) + 600; // 10 min

      const result = await swap(
        payToken.address,
        receiveToken.address,
        amount,
        selectedQuote.pool_address,
        deadline,
      );

      if (result?.success) {
        setSwapState("success");
        setTxHash(result.hash);
        void refreshBalances();
        void recordActivity({
          txHash: result.hash,
          action: "swap",
          status: "success",
          amounts: { [payToken.symbol.toLowerCase()]: parsedPay },
        });
        setTimeout(() => { setSwapState("idle"); setTxHash(null); setPayAmount(""); }, 4000);
      } else {
        setSwapState("error");
        setSwapError(result?.error ?? "Swap failed");
        void recordActivity({
          txHash: "",
          action: "swap",
          status: "error",
          errorMessage: result?.error ?? "Swap failed",
        });
      }
    } catch (err) {
      setSwapState("error");
      const msg = err instanceof Error ? err.message : "Swap failed";
      setSwapError(msg);
      void recordActivity({ txHash: "", action: "swap", status: "error", errorMessage: msg });
    }
  };

  // ── Button label/state ──────────────────────────────────
  const buttonConfig = useMemo(() => {
    if (!isConnected) return { label: "CONNECT WALLET", disabled: true, bg: "var(--surface-hi)" };
    if (!parsedPay) return { label: "ENTER AMOUNT", disabled: true, bg: "var(--surface-hi)" };
    if (quotesLoading) return { label: "FETCHING QUOTES...", disabled: true, bg: "var(--surface-hi)" };
    if (quotes.length === 0 && parsedPay > 0 && !quotesLoading) return { label: "NO ROUTE AVAILABLE", disabled: true, bg: "#2a1515" };
    if (parsedPay * TOKEN_UNIT > Number(payBalance)) return { label: "INSUFFICIENT BALANCE", disabled: true, bg: "#2a1515" };
    if (swapState === "signing") return { label: "CONFIRM WITH BIOMETRIC...", disabled: true, bg: "var(--accent)" };
    if (swapState === "submitted") return { label: "SUBMITTING...", disabled: true, bg: "var(--accent)" };
    if (swapState === "success") return { label: "SWAP COMPLETE ✓", disabled: true, bg: "#166534" };
    if (swapState === "error") return { label: "RETRY SWAP", disabled: false, bg: "#7f1d1d" };
    return { label: `SWAP ${payToken.symbol} FOR ${receiveToken.symbol}`, disabled: false, bg: "var(--accent)" };
  }, [isConnected, parsedPay, quotesLoading, quotes.length, payBalance, swapState, payToken.symbol, receiveToken.symbol]);

  return (
    <div className="flex justify-center anim-fadeSlideInUp-300ms" style={{ animationDelay: "0.3s" }}>
      <div className="w-full max-w-[420px] space-y-[6px]">

        {/* ── Pay ──────────────────────────────── */}
        <div className="rounded-[var(--r)] px-[16px] py-[14px]" style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }}>
          <div className="flex items-center justify-between mb-[10px]">
            <span className="text-[10px] tracking-[0.1em] font-medium" style={{ color: "var(--text-secondary)" }}>YOU PAY</span>
            <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
              Balance: <span style={{ color: "var(--text-primary)" }}>{isConnected ? fmtBalance(payBalance) : "—"}</span> {payToken.symbol}
            </span>
          </div>
          <div className="flex items-center gap-[12px]">
            <TokenDropdown selected={payToken} exclude={receiveToken} onSelect={setPayToken}
              open={payDropdownOpen} setOpen={setPayDropdownOpen} label="Pay token" />
            <input type="text" inputMode="decimal" aria-label="Amount to pay"
              className="bg-transparent text-right text-[22px] font-medium flex-1 min-w-0 focus:outline-none"
              style={{ color: "var(--text-primary)" }} placeholder="0"
              value={payAmount} onChange={(e) => { setPayAmount(e.target.value.replace(/[^0-9.]/g, "")); }} />
            <button className="text-[8px] tracking-[0.1em] font-bold px-[6px] py-[3px] rounded-[3px] shrink-0 transition-colors hover:brightness-110"
              style={{ background: "var(--accent-dim)", color: "var(--accent)" }}
              onClick={() => { if (isConnected) setPayAmount(String(Number(payBalance) / TOKEN_UNIT)); }}
              aria-label="Use maximum balance">MAX</button>
          </div>
        </div>

        {/* ── Flip ─────────────────────────────── */}
        <div className="flex justify-center -my-[6px] relative z-10">
          <button onClick={handleFlip} aria-label="Swap tokens"
            className="w-[30px] h-[30px] rounded-full flex items-center justify-center transition-all duration-150 hover:scale-110"
            style={{ background: "var(--surface-hi)", border: "1px solid var(--border-default)" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "var(--text-secondary)" }} aria-hidden="true">
              <path d="M7 4v16M7 20l-4-4m4 4l4-4M17 20V4m0 0l4 4m-4-4l-4 4" />
            </svg>
          </button>
        </div>

        {/* ── Receive ──────────────────────────── */}
        <div className="rounded-[var(--r)] px-[16px] py-[14px]" style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }}>
          <div className="flex items-center justify-between mb-[10px]">
            <span className="text-[10px] tracking-[0.1em] font-medium" style={{ color: "var(--text-secondary)" }}>YOU RECEIVE</span>
            <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
              Balance: <span style={{ color: "var(--text-primary)" }}>{isConnected ? fmtBalance(receiveBalance) : "—"}</span> {receiveToken.symbol}
            </span>
          </div>
          <div className="flex items-center gap-[12px]">
            <TokenDropdown selected={receiveToken} exclude={payToken} onSelect={setReceiveToken}
              open={receiveDropdownOpen} setOpen={setReceiveDropdownOpen} label="Receive token" />
            <input type="text" aria-label="Amount to receive"
              className="bg-transparent text-right text-[22px] font-medium flex-1 min-w-0 focus:outline-none"
              style={{ color: receiveAmount !== "0.00" ? "var(--green)" : "var(--text-secondary)" }} placeholder="0"
              value={receiveAmount} readOnly />
          </div>
        </div>

        {/* ── Route selector ───────────────────── */}
        {(sortedQuotes.length > 0 || quotesLoading) && (
          <div className="rounded-[var(--r)] px-[16px] py-[14px]" style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }}
            role="radiogroup" aria-label="Select swap protocol">
            <div className="flex items-center justify-between mb-[10px]">
              <span className="text-[10px] tracking-[0.1em] font-medium" style={{ color: "var(--text-secondary)" }}>ROUTE VIA</span>
              <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                {quotesLoading ? "Fetching..." : "Best rate auto-selected"}
              </span>
            </div>
            <div className="flex gap-[6px]">
              {quotesLoading ? (
                <div className="flex-1 h-[80px] rounded-[var(--r)] animate-pulse" style={{ background: "var(--surface-hi)" }} />
              ) : (
                sortedQuotes.map((quote, i) => {
                  const adapterId = Number(quote.adapter_id);
                  const meta = ADAPTER_META[adapterId] ?? { name: `Protocol ${adapterId}`, logo: "", fee: "—" };
                  const isSelected = selectedQuote?.pool_address === quote.pool_address;
                  const isBest = i === 0;
                  const outputAmt = (Number(quote.amount_out) / TOKEN_UNIT).toFixed(2);
                  const quoteRate = Number(quote.amount_in) > 0 ? (Number(quote.amount_out) / Number(quote.amount_in)).toFixed(4) : "—";

                  return (
                    <button key={quote.pool_address} role="radio" aria-checked={isSelected}
                      aria-label={`${meta.name}: ${outputAmt} ${receiveToken.symbol}`}
                      onClick={() => { setSelectedQuote(quote); }}
                      className="flex-1 rounded-[var(--r)] p-[10px] transition-all duration-100 relative text-left"
                      style={{
                        background: isSelected ? "rgba(255,255,255,0.04)" : "transparent",
                        border: isSelected ? "1px solid var(--border-strong)" : "1px solid var(--border-subtle)",
                      }}>
                      {isBest && (
                        <span className="absolute top-0 right-0 text-[7px] tracking-[0.06em] font-bold px-[5px] py-[2px] rounded-bl-[4px] rounded-tr-[var(--r)]"
                          style={{ background: "var(--accent)", color: "var(--bg)" }}>BEST</span>
                      )}
                      <div className="flex items-center gap-[6px] mb-[8px]">
                        {meta.logo && <Image src={meta.logo} alt="" width={20} height={20} className="rounded-full shrink-0" />}
                        <span className="text-[10px] font-medium leading-none"
                          style={{ color: isSelected ? "var(--text-primary)" : "var(--text-secondary)" }}>{meta.name}</span>
                      </div>
                      <div className="text-[14px] font-semibold leading-tight mb-[4px]"
                        style={{ color: isSelected ? "var(--text-primary)" : "var(--text-secondary)" }}>
                        {outputAmt}
                        <span className="text-[9px] font-normal ml-[4px]" style={{ color: "var(--text-secondary)" }}>{receiveToken.symbol}</span>
                      </div>
                      <div className="text-[9px]" style={{ color: "var(--text-secondary)" }}>
                        Fee {meta.fee} · Rate {quoteRate}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── Rate + Slippage ──────────────────── */}
        <div className="rounded-[var(--r)] px-[16px] py-[10px] flex items-center justify-between flex-wrap gap-y-2"
          style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }}>
          <div className="flex items-center gap-[6px]">
            <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
              1 {payToken.symbol} = {rate} {receiveToken.symbol}
            </span>
          </div>
          <div className="flex items-center gap-[4px]" role="group" aria-label="Slippage tolerance">
            {SLIPPAGE_PRESETS.map((val) => (
              <button key={val} onClick={() => { setSlippageTolerance(val); }} aria-pressed={slippageTolerance === val}
                className="text-[9px] font-medium h-[20px] px-[7px] rounded-[3px] transition-colors"
                style={{
                  background: slippageTolerance === val ? "var(--accent)" : "transparent",
                  color: slippageTolerance === val ? "var(--bg)" : "var(--text-secondary)",
                  border: slippageTolerance === val ? "1px solid var(--accent)" : "1px solid var(--border-subtle)",
                }}>{val}%</button>
            ))}
          </div>
        </div>

        {/* ── Swap button ──────────────────────── */}
        <button onClick={() => { void handleSwap(); }} disabled={buttonConfig.disabled}
          className="w-full h-[42px] rounded-[var(--r)] text-[11px] tracking-[0.15em] font-semibold transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 active:scale-[0.995]"
          style={{ background: buttonConfig.bg, color: buttonConfig.bg === "var(--accent)" ? "var(--bg)" : "var(--text-primary)" }}
          aria-label={buttonConfig.label}>
          {swapState === "signing" && <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-2 align-middle" />}
          {buttonConfig.label}
        </button>

        {/* ── Error message ─────────────────────── */}
        {swapState === "error" && swapError && (
          <div className="rounded-[var(--r)] px-[14px] py-[10px] text-[10px]" style={{ background: "#1a0d0d", border: "1px solid #3a1515", color: "#e05252" }}>
            {swapError}
          </div>
        )}

        {/* ── Success message ──────────────────── */}
        {swapState === "success" && txHash && (
          <div className="rounded-[var(--r)] px-[14px] py-[10px] text-[10px]" style={{ background: "#0d1a0d", border: "1px solid #153a15", color: "#4ade80" }}>
            Swap complete!{" "}
            <a href={`https://stellar.expert/explorer/testnet/tx/${txHash}`} target="_blank" rel="noreferrer" className="underline">
              View transaction
            </a>
          </div>
        )}

        {/* ── Transaction details ──────────────── */}
        {selectedQuote && (
          <div className="rounded-[var(--r)] px-[16px] py-[12px] space-y-[8px]" style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }}>
            {[
              { label: "Slippage Tolerance", value: `${slippageTolerance}%` },
              { label: "Minimum Received", value: `${minReceived} ${receiveToken.symbol}` },
              { label: "Network Fee", value: "Sponsored", color: "var(--green)" },
              { label: "Exchange Fee", value: ADAPTER_META[Number(selectedQuote.adapter_id)]?.fee ?? "—" },
              { label: "Route", value: `${payToken.symbol} → ${receiveToken.symbol}`,
                extra: ADAPTER_META[Number(selectedQuote.adapter_id)] },
            ].map((row: { label: string; value: string; color?: string; extra?: { name: string; logo: string } }) => (
              <div key={row.label} className="flex items-center justify-between">
                <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{row.label}</span>
                <span className="inline-flex items-center gap-[5px] text-[10px] font-medium" style={{ color: row.color ?? "var(--text-primary)" }}>
                  {row.extra?.logo && <Image src={row.extra.logo} alt="" width={12} height={12} className="rounded-full" />}
                  {row.value}
                  {row.extra && <span className="text-[9px] font-normal" style={{ color: "var(--text-secondary)" }}>via {row.extra.name}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
